/**
 * Quality — the only screen in this console that answers "was it any good".
 *
 * Every other page reports what happened: messages sent, leads moved, handovers opened, rupees
 * spent. None of them can tell a good reply from a bad one, and the system this product replaces
 * could not either — 456 handovers, 97% never actioned, not one marked won or lost, and a guard set
 * nobody could show was firing.
 *
 * Four kinds of evidence, deliberately not blended into a single score:
 *
 *  1. **A person's opinion.** Thumbs on a bubble, and a correction saying what it should have said.
 *     The correction cannot be closed without a disposition and a reason; this screen shows that
 *     refusal rather than hiding the button, because the refusal is the feature.
 *  2. **A judge's opinion of a sample.** Five fixed dimensions, 5–10% of last night's turns, with
 *     the scorecard version, the prompt digest and the model beside every number — and failures
 *     counted as failures, never as zeros.
 *  3. **The rules' own record.** Guard fires as a share of the turns that REACHED each guard, plus
 *     the guards that have refused nothing for seven days.
 *  4. **The replays.** The golden gate and the Persona Lab, which A11 runs and this only shows.
 *
 * Nothing here changes what the agent does. That is Config Studio's job, where a change is a
 * proposal with a diff, an approver and a digest to roll back to.
 *
 * Author: Danish Khan
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { Button } from '../components/Button.tsx';
import { Pill } from '../components/Pill.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { formatDateTime, formatInr } from '../lib/format.ts';
import { canCloseCorrection, canRateReplies, roleGate } from '../lib/pii.ts';
import { useUser } from '../lib/session.tsx';
import {
  hoursLabel, qualityApi, rateLabel,
  type Correction, type Disposition, type GuardRate, type QualityClient,
} from './qualityApi.ts';

const INPUT = 'h-9 w-full rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 text-sm text-fg';
const AREA = 'w-full rounded-[var(--h1-radius)] border border-line-strong bg-bg p-2 text-sm text-fg';
const CARD = 'rounded-[var(--h1-radius)] border border-line bg-surface p-4';

type Tab = 'review' | 'corrections' | 'sampling' | 'guards' | 'replays';

const TABS: { id: Tab; label: string }[] = [
  { id: 'review', label: 'Review replies' },
  { id: 'corrections', label: 'Corrections' },
  { id: 'sampling', label: 'Sampled QA' },
  { id: 'guards', label: 'Guards' },
  { id: 'replays', label: 'Golden & personas' },
];

export interface QualityProps {
  /** Injected in tests. The page is otherwise pointed at `/api/v1/quality`. */
  client?: QualityClient;
}

export function QualityPage({ client = qualityApi }: QualityProps = {}): ReactNode {
  const qc = useQueryClient();
  const user = useUser();
  const [tab, setTab] = useState<Tab>('review');
  const [failed, setFailed] = useState<string | null>(null);

  /**
   * Two gates, not one, because this screen holds two different decisions.
   *
   * A rep MAY say a reply was wrong and write what it should have said — that is the evidence, and
   * the person who reads the thread all day is the person who has it. A rep may NOT decide what
   * that evidence means: closing a correction as `rule`, `test` or `wontfix` is a manager's call,
   * and the API says so in two separate declarations. A single page-level lock would either take
   * the thumbs away from the floor or hand the dispositions to it, and both are wrong.
   */
  const mayRate = canRateReplies(user.role);
  const rateGate = roleGate(
    mayRate,
    'Rating a reply and writing a correction need a role of rep or above',
    user.role,
  );
  const mayClose = canCloseCorrection(user.role);
  const closeGate = roleGate(
    mayClose,
    'Closing a correction — deciding it is a rule, a test or a wontfix — is for a manager or an owner',
    user.role,
  );

  const summary = useQuery({ queryKey: ['quality', 'summary'], queryFn: () => client.summary() });
  const refresh = async (): Promise<void> => { await qc.invalidateQueries({ queryKey: ['quality'] }); };

  if (summary.isError) {
    return <ErrorState error={summary.error} context="open Quality" onRetry={() => void summary.refetch()} />;
  }
  if (!summary.data) return <p className="p-5 text-sm text-fg-muted">Reading what the agent has been saying…</p>;
  const s = summary.data;
  /**
   * Guards that have refused nothing — counted once each.
   *
   * `silent` and `neverReached` come from two independent predicates over the same guard list
   * (`routes/quality.ts`), and a guard nothing reached is necessarily also a guard that never
   * fired. Adding the two array lengths counted it twice in the headline tile.
   */
  const quietGuards = new Set([...s.guards.silent, ...s.guards.neverReached]);

  return (
    <div className="h1-scroll h-full overflow-y-auto px-5 py-5">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <header className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold text-fg">Quality</h1>
          <p className="text-sm text-fg-muted">
            What people thought, what a judge thought of a sample, what the guards refused, and what the
            replays still say. Nothing on this page changes the agent — a change is a Config Studio proposal.
          </p>
        </header>

        <section className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Quality at a glance">
          <Tile
            label="Open corrections"
            value={String(s.corrections.open)}
            note={s.corrections.oldestOpenHours === null
              ? 'nothing waiting'
              : `oldest ${hoursLabel(s.corrections.oldestOpenHours)}`}
            tone={s.corrections.open > 0 ? 'warn' : 'ok'}
          />
          <Tile
            label={`QA score, ${s.qa.days} d`}
            value={s.qa.overall === null ? '—' : s.qa.overall.toFixed(2)}
            note={s.qa.scored === 0
              ? 'nothing has been sampled yet'
              : `${s.qa.scored} judged · ${s.qa.failed} failed`}
          />
          <Tile
            label={`Thumbs, ${s.thumbs.days} d`}
            value={`${s.thumbs.up} up · ${s.thumbs.down} down`}
            note={s.thumbs.down === 0
              ? 'no thumbs down'
              : `${s.thumbs.downWithCorrection} of ${s.thumbs.down} became a correction`}
          />
          {/* The window is the server's, not a literal: "7 d" was hard-coded beside two other
              tiles that already read theirs off the response. */}
          <Tile
            label={`Guards silent, ${s.guards.days} d`}
            value={String(quietGuards.size)}
            note={quietGuards.size === 0
              ? 'every guard has refused something'
              : 'a guard that never fires is unnecessary or broken'}
            tone={quietGuards.size > 0 ? 'warn' : 'ok'}
          />
        </section>

        {failed && (
          <p role="alert" className="rounded-[var(--h1-radius)] bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
            {failed}
          </p>
        )}

        <nav className="flex flex-wrap gap-1 border-b border-line" aria-label="Quality sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => setTab(t.id)}
              className={clsx(
                'h-9 rounded-t-[var(--h1-radius)] px-3 text-sm font-medium',
                tab === t.id ? 'bg-surface text-fg border border-line border-b-transparent' : 'text-fg-muted hover:text-fg',
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === 'review' && <ReviewTab client={client} onFail={setFailed} onDone={refresh} gate={rateGate} />}
        {tab === 'corrections' && (
          <CorrectionsTab
            client={client}
            dispositions={s.corrections.dispositions}
            ruleWithoutProposal={s.corrections.ruleWithoutProposal}
            onFail={setFailed}
            onDone={refresh}
            gate={closeGate}
          />
        )}
        {tab === 'sampling' && <SamplingTab client={client} summary={s} />}
        {tab === 'guards' && <GuardsTab rates={s.guards.rates} denominator={s.guards.denominator} days={s.guards.days} />}
        {tab === 'replays' && <ReplaysTab golden={s.golden} personas={s.personas} />}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------- pieces */

function Tile({ label, value, note, tone }: { label: string; value: string; note: string; tone?: 'ok' | 'warn' }): ReactNode {
  return (
    <div className={CARD}>
      <p className="text-xs font-medium text-fg-muted">{label}</p>
      <p className={clsx('pt-1 text-xl font-semibold', tone === 'warn' ? 'text-warn-soft-fg' : 'text-fg')}>{value}</p>
      <p className="pt-1 text-xs text-fg-subtle">{note}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ review replies */

function ReviewTab(
  { client, onFail, onDone, gate }: {
    client: QualityClient; onFail: (m: string | null) => void; onDone: () => Promise<void>;
    /** Why this role may not rate or correct, or undefined when it may. */
    gate: string | undefined;
  },
): ReactNode {
  const [filter, setFilter] = useState<'all' | 'flagged' | 'unrated'>('flagged');
  const [writing, setWriting] = useState<{ turnId: string; messageId: string | null; said: string } | null>(null);
  const [should, setShould] = useState('');
  const [why, setWhy] = useState('');

  const replies = useQuery({ queryKey: ['quality', 'replies', filter], queryFn: () => client.replies(filter) });
  const thumb = useMutation({
    mutationFn: (v: { turnId: string; messageId: string | null; verdict: 'up' | 'down' }) => client.thumb(v),
    onSuccess: async () => { onFail(null); await onDone(); },
    onError: (e: Error) => onFail(e.message),
  });
  const correct = useMutation({
    mutationFn: (v: { turnId: string; messageId: string | null; shouldHaveSaid: string; why: string }) =>
      client.correct(v),
    onSuccess: async () => { setWriting(null); setShould(''); setWhy(''); onFail(null); await onDone(); },
    onError: (e: Error) => onFail(e.message),
  });

  if (replies.isError) {
    return <ErrorState error={replies.error} context="list recent replies" onRetry={() => void replies.refetch()} />;
  }
  const rows = replies.data ?? [];

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        {(['flagged', 'unrated', 'all'] as const).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? 'primary' : 'secondary'} onClick={() => setFilter(f)}>
            {f === 'flagged' ? 'Guard refused or thumbed down' : f === 'unrated' ? 'Not yet rated' : 'Everything'}
          </Button>
        ))}
      </div>

      {/*
        Said once, in words, rather than only on hover: reading every reply the agent sent is the
        whole point of this tab and stays open to every role, and the filters above keep working.
      */}
      {gate && (
        <p role="status" className="rounded-[var(--h1-radius)] bg-neutral-soft px-3 py-2 text-xs text-neutral-soft-fg">
          {gate} Reading what it said is open to everybody.
        </p>
      )}

      {rows.length === 0 && (
        <EmptyState
          title="No replies to review here"
          body="Either nothing matched this filter in the last week, or this tenant has not answered anybody yet."
        />
      )}

      {rows.map((r) => (
        <article key={r.turnId} className={CARD}>
          <header className="flex flex-wrap items-center gap-2 pb-2">
            <span className="text-sm font-semibold text-fg">{r.displayName}</span>
            <Pill tone="neutral">{formatDateTime(r.startedAt)}</Pill>
            {r.language && <Pill tone="info">{r.language}</Pill>}
            {r.status !== 'sent' && <Pill tone="warn">{r.status}</Pill>}
            {r.guards.map((g) => (
              <Pill key={`${g.guard}-${g.verdict}`} tone={g.verdict === 'block' ? 'danger' : 'warn'}>
                {g.guard} · {g.verdict}
              </Pill>
            ))}
            {r.qa && (
              <Pill tone={r.qa.outcome === 'scored' ? 'brand' : 'warn'}>
                {r.qa.outcome === 'scored' ? `judged ${r.qa.overall?.toFixed(1)}` : `judge: ${r.qa.outcome}`}
              </Pill>
            )}
          </header>

          {r.customer.map((c, i) => (
            <p key={i} className="pb-1 text-sm text-fg-muted">
              <span className="pr-2 text-xs uppercase text-fg-subtle">customer</span>
              {c}
            </p>
          ))}

          <ul className="flex flex-col gap-2 pt-1">
            {r.bubbles.map((b) => (
              <li key={b.id} className="flex flex-wrap items-start gap-2">
                <p className="min-w-0 flex-1 text-sm text-fg">{b.text}</p>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={Boolean(gate)}
                  title={gate}
                  aria-label={`Good reply: ${b.text.slice(0, 40)}`}
                  onClick={() => thumb.mutate({ turnId: r.turnId, messageId: b.id, verdict: 'up' })}
                >
                  👍
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={Boolean(gate)}
                  title={gate}
                  aria-label={`Bad reply: ${b.text.slice(0, 40)}`}
                  onClick={() => thumb.mutate({ turnId: r.turnId, messageId: b.id, verdict: 'down' })}
                >
                  👎
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={Boolean(gate)}
                  title={gate}
                  onClick={() => { setWriting({ turnId: r.turnId, messageId: b.id, said: b.text }); setShould(''); setWhy(''); }}
                >
                  Correct
                </Button>
              </li>
            ))}
            {r.bubbles.length === 0 && (
              <li className="text-sm text-fg-subtle">This turn sent nothing — rate the turn itself.</li>
            )}
          </ul>

          <footer className="flex items-center gap-3 pt-2 text-xs text-fg-subtle">
            <span>{r.thumbs.up} up · {r.thumbs.down} down</span>
            <span>{r.corrections} correction{r.corrections === 1 ? '' : 's'}</span>
          </footer>

          {writing?.turnId === r.turnId && (
            <form
              className="flex flex-col gap-2 pt-3"
              onSubmit={(e) => {
                e.preventDefault();
                correct.mutate({ turnId: writing.turnId, messageId: writing.messageId, shouldHaveSaid: should, why });
              }}
            >
              <p className="text-xs text-fg-subtle">It said: “{writing.said}”</p>
              <label className="text-xs font-medium text-fg-muted" htmlFor={`should-${r.turnId}`}>
                What it should have said
              </label>
              <textarea
                id={`should-${r.turnId}`}
                className={AREA}
                rows={3}
                value={should}
                onChange={(e) => setShould(e.target.value)}
              />
              <label className="text-xs font-medium text-fg-muted" htmlFor={`why-${r.turnId}`}>Why (optional)</label>
              <input id={`why-${r.turnId}`} className={INPUT} value={why} onChange={(e) => setWhy(e.target.value)} />
              <p className="text-xs text-fg-subtle">
                Recording this changes nothing by itself. A manager dispositions it, and if a rule should
                change, that change is a Config Studio proposal.
              </p>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={Boolean(gate) || should.trim().length === 0} title={gate}>
                  Save correction
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setWriting(null)}>Cancel</Button>
              </div>
            </form>
          )}
        </article>
      ))}
    </section>
  );
}

/* --------------------------------------------------------------------- corrections */

function CorrectionsTab({
  client, dispositions, ruleWithoutProposal, onFail, onDone, gate,
}: {
  client: QualityClient;
  dispositions: { id: string; means: string }[];
  ruleWithoutProposal: number;
  onFail: (m: string | null) => void;
  onDone: () => Promise<void>;
  /** Why this role may not close a correction, or undefined when it may. */
  gate: string | undefined;
}): ReactNode {
  const [status, setStatus] = useState<'open' | 'closed'>('open');
  const [draft, setDraft] = useState<Record<string, { disposition: Disposition | ''; reason: string }>>({});
  const list = useQuery({ queryKey: ['quality', 'corrections', status], queryFn: () => client.corrections(status) });
  const close = useMutation({
    mutationFn: (v: { id: string; disposition: Disposition; reason: string }) =>
      client.close(v.id, { disposition: v.disposition, reason: v.reason }),
    onSuccess: async () => { onFail(null); await onDone(); },
    onError: (e: Error) => onFail(e.message),
  });

  if (list.isError) {
    return <ErrorState error={list.error} context="load the corrections queue" onRetry={() => void list.refetch()} />;
  }
  const rows = list.data ?? [];
  const at = (id: string): { disposition: Disposition | ''; reason: string } =>
    draft[id] ?? { disposition: '', reason: '' };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button size="sm" variant={status === 'open' ? 'primary' : 'secondary'} onClick={() => setStatus('open')}>Open</Button>
        <Button size="sm" variant={status === 'closed' ? 'primary' : 'secondary'} onClick={() => setStatus('closed')}>Closed</Button>
      </div>

      {/*
        The queue itself stays open to every role — a rep who wrote a correction should be able to
        see what was decided about it, and an operator diagnosing the agent needs the same list.
        What the gate takes away is the disposition, and it says so before the form, not after it.
      */}
      {gate && (
        <p role="status" className="rounded-[var(--h1-radius)] bg-neutral-soft px-3 py-2 text-xs text-neutral-soft-fg">
          {gate} You can read every correction and what it was closed as.
        </p>
      )}

      <div className={CARD}>
        <p className="text-sm font-medium text-fg">A correction closes one of three ways, and never without a reason.</p>
        <ul className="pt-2 text-sm text-fg-muted">
          {dispositions.map((d) => (
            <li key={d.id} className="pb-1">
              <span className="font-medium text-fg">{d.id}</span> — {d.means}
            </li>
          ))}
        </ul>
        {ruleWithoutProposal > 0 && (
          <p className="pt-2 text-xs text-warn-soft-fg">
            {ruleWithoutProposal} closed as “rule” with no Config Studio proposal linked yet — the decision was
            made, the change has not been.
          </p>
        )}
      </div>

      {rows.length === 0 && (
        <EmptyState
          title={status === 'open' ? 'Nothing waiting' : 'Nothing closed yet'}
          body={status === 'open'
            ? 'Every correction anybody has written has been dispositioned.'
            : 'A correction closes with a disposition and a reason; none has yet.'}
        />
      )}

      {rows.map((c: Correction) => (
        <article key={c.id} className={CARD}>
          <header className="flex flex-wrap items-center gap-2 pb-2">
            <Pill tone={c.status === 'open' ? 'warn' : 'ok'}>{c.status}</Pill>
            <span className="text-xs text-fg-subtle">{c.author}</span>
            <span className="text-xs text-fg-subtle">{formatDateTime(c.at)}</span>
            {c.status === 'open' && <span className="text-xs text-fg-subtle">open {hoursLabel(c.openHours)}</span>}
            {c.disposition && <Pill tone="brand">{c.disposition}</Pill>}
          </header>
          <p className="text-sm text-fg-muted"><span className="pr-2 text-xs uppercase text-fg-subtle">said</span>{c.said}</p>
          <p className="pt-1 text-sm text-fg"><span className="pr-2 text-xs uppercase text-fg-subtle">should have said</span>{c.shouldHaveSaid}</p>
          {c.why && <p className="pt-1 text-xs text-fg-subtle">{c.why}</p>}
          {c.status === 'closed' && (
            <p className="pt-2 text-sm text-fg-muted">
              Closed by {c.closedBy} as <span className="font-medium text-fg">{c.disposition}</span>: {c.dispositionReason}
              {c.proposalId && <> · proposal {c.proposalId}</>}
              {c.goldenCase && <> · golden case {c.goldenCase}</>}
            </p>
          )}

          {c.status === 'open' && (
            <form
              className="flex flex-col gap-2 pt-3 md:flex-row md:items-center"
              onSubmit={(e) => {
                e.preventDefault();
                const d = at(c.id);
                if (d.disposition === '') return;
                close.mutate({ id: c.id, disposition: d.disposition, reason: d.reason });
              }}
            >
              <label className="sr-only" htmlFor={`disposition-${c.id}`}>Disposition</label>
              <select
                id={`disposition-${c.id}`}
                className={clsx(INPUT, 'md:w-40 disabled:opacity-50')}
                disabled={Boolean(gate)}
                title={gate}
                value={at(c.id).disposition}
                onChange={(e) => setDraft((d) => ({ ...d, [c.id]: { ...at(c.id), disposition: e.target.value as Disposition | '' } }))}
              >
                <option value="">Choose a disposition…</option>
                {dispositions.map((d) => <option key={d.id} value={d.id}>{d.id}</option>)}
              </select>
              <label className="sr-only" htmlFor={`reason-${c.id}`}>Reason</label>
              <input
                id={`reason-${c.id}`}
                className={clsx(INPUT, 'disabled:opacity-50')}
                disabled={Boolean(gate)}
                title={gate}
                placeholder="Why this disposition — required"
                value={at(c.id).reason}
                onChange={(e) => setDraft((d) => ({ ...d, [c.id]: { ...at(c.id), reason: e.target.value } }))}
              />
              <Button
                type="submit"
                size="sm"
                disabled={Boolean(gate) || at(c.id).disposition === '' || at(c.id).reason.trim().length === 0}
                title={gate}
              >
                Close
              </Button>
            </form>
          )}
        </article>
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------- sampled QA */

function SamplingTab(
  { client, summary }: { client: QualityClient; summary: { qa: { dimensions: { name: string; means: string }[]; means: Record<string, number | null>; flags: { flag: string; n: number }[]; provenance: { scorecardVersion: string; promptVersion: string; model: string; n: number }[]; byLanguage: { language: string; n: number; overall: number | null }[]; scored: number; failed: number; days: number } } },
): ReactNode {
  const qa = useQuery({ queryKey: ['quality', 'qa'], queryFn: () => client.qa() });
  if (qa.isError) return <ErrorState error={qa.error} context="load sampled QA" onRetry={() => void qa.refetch()} />;
  if (!qa.data) return <p className="p-3 text-sm text-fg-muted">Reading last night's sample…</p>;
  const v = qa.data;

  return (
    <section className="flex flex-col gap-3">
      <div className={CARD}>
        <p className="text-sm font-medium text-fg">
          {v.settings.percent}% of each night's replies are read by the reasoner's high tier, capped at{' '}
          {v.settings.maxPercent}% and at {v.settings.maxPerNight} judgements, at {v.settings.hour} local.
        </p>
        <p className="pt-1 text-sm text-fg-muted">{v.estimate.basis}</p>
        <p className="pt-1 text-xs text-fg-subtle">
          Tonight: {v.estimate.target} of {v.estimate.population} eligible turns ·{' '}
          {v.estimate.costInr === null
            ? 'cost unknown — no judge rate is configured, which is not the same as free'
            : `about ${formatInr(v.estimate.costInr, true)}`}
        </p>
      </div>

      <div className={CARD}>
        <h2 className="pb-2 text-sm font-semibold text-fg">The scorecard ({v.settings.scorecardVersion})</h2>
        <dl className="grid gap-2 md:grid-cols-5">
          {summary.qa.dimensions.map((d) => (
            <div key={d.name}>
              <dt className="text-xs font-medium text-fg-muted">{d.name}</dt>
              <dd className="text-lg font-semibold text-fg">
                {summary.qa.means[d.name] === null || summary.qa.means[d.name] === undefined
                  ? '—'
                  : (summary.qa.means[d.name] as number).toFixed(2)}
              </dd>
              <dd className="text-xs text-fg-subtle">{d.means}</dd>
            </div>
          ))}
        </dl>
        <p className="pt-2 text-xs text-fg-subtle">
          {summary.qa.scored} judged, {summary.qa.failed} failed over {summary.qa.days} days. A failed judgement is
          recorded as a failure and never averaged in as a zero.
        </p>
      </div>

      {summary.qa.byLanguage.length > 0 && (
        <div className={CARD}>
          <h2 className="pb-2 text-sm font-semibold text-fg">By the language the reply was in</h2>
          <ul className="flex flex-wrap gap-4 text-sm text-fg-muted">
            {summary.qa.byLanguage.map((l) => (
              <li key={l.language}>
                <span className="font-medium text-fg">{l.language}</span> · {l.n} judged ·{' '}
                {l.overall === null ? '—' : l.overall.toFixed(2)}
              </li>
            ))}
          </ul>
          <p className="pt-2 text-xs text-fg-subtle">
            English, Hindi and Hinglish are scored by the same five questions. A judge marking one language down
            would show here as a lower row, which is what this breakdown is for.
          </p>
        </div>
      )}

      {summary.qa.provenance.length > 0 && (
        <div className={CARD}>
          <h2 className="pb-2 text-sm font-semibold text-fg">Where these numbers came from</h2>
          <ul className="text-sm text-fg-muted">
            {summary.qa.provenance.map((p) => (
              <li key={`${p.scorecardVersion}-${p.promptVersion}-${p.model}`}>
                {p.n} judgements · scorecard {p.scorecardVersion} · prompt {p.promptVersion} · {p.model}
              </li>
            ))}
          </ul>
          {summary.qa.provenance.length > 1 && (
            <p className="pt-2 text-xs text-warn-soft-fg">
              More than one scorecard or prompt in this window — the average above mixes them.
            </p>
          )}
        </div>
      )}

      <div className={CARD}>
        <h2 className="pb-2 text-sm font-semibold text-fg">Nights</h2>
        {v.nights.length === 0
          ? <p className="text-sm text-fg-muted">No night has been sampled yet.</p>
          : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-fg-subtle">
                <tr><th className="py-1">Night</th><th>Eligible</th><th>Judged</th><th>Failed</th><th>Cost</th><th>Note</th></tr>
              </thead>
              <tbody>
                {v.nights.map((n) => (
                  <tr key={n.localDay} className="border-t border-line">
                    <td className="py-1 text-fg">{n.localDay}</td>
                    <td className="text-fg-muted">{n.population}</td>
                    <td className="text-fg-muted">{n.scored} of {n.target}</td>
                    <td className={n.failed > 0 ? 'text-warn-soft-fg' : 'text-fg-muted'}>{n.failed}</td>
                    <td className="text-fg-muted">{n.costInr === null ? '—' : formatInr(n.costInr, true)}</td>
                    <td className="text-fg-subtle">{n.skipped ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {summary.qa.flags.length > 0 && (
        <div className={CARD}>
          <h2 className="pb-2 text-sm font-semibold text-fg">What the judge flagged</h2>
          <div className="flex flex-wrap gap-2">
            {summary.qa.flags.map((f) => <Pill key={f.flag} tone="warn">{f.flag} · {f.n}</Pill>)}
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------------- guards */

function GuardsTab(
  { rates, denominator, days }: { rates: GuardRate[]; denominator: string; days: number },
): ReactNode {
  const widest = Math.max(1, ...rates.map((r) => r.ratePercent ?? 0));
  return (
    <section className="flex flex-col gap-3">
      <div className={CARD}>
        <h2 className="text-sm font-semibold text-fg">Guard fire rate, last {days} days</h2>
        <p className="pt-1 text-sm text-fg-muted">
          Every bar is a share of <span className="font-medium text-fg">{denominator}</span>, never of turns overall.
          A guard late in the chain sees fewer turns, and a raw count reads as alarming when it is normal.
        </p>
      </div>

      <div className={CARD}>
        <ul className="flex flex-col gap-2">
          {rates.map((g) => (
            <li key={g.guard} className="flex flex-wrap items-center gap-3">
              <span className="w-44 shrink-0 text-sm text-fg">{g.guard}</span>
              <span className="h-2 min-w-0 flex-1 rounded-full bg-surface-2" aria-hidden>
                <span
                  className={clsx('block h-2 rounded-full', g.neverReached ? 'bg-neutral-soft' : 'bg-brand')}
                  style={{ width: `${((g.ratePercent ?? 0) / widest) * 100}%` }}
                />
              </span>
              <span className="w-56 shrink-0 text-right text-xs text-fg-muted">{rateLabel(g)}</span>
              {g.neverReached && <Pill tone="danger">nothing reached it</Pill>}
              {g.silentSevenDays && <Pill tone="warn">no fires in {days} d</Pill>}
            </li>
          ))}
        </ul>
      </div>

      {(rates.some((g) => g.silentSevenDays) || rates.some((g) => g.neverReached)) && (
        <div className={CARD}>
          <h2 className="text-sm font-semibold text-fg">Needs a human</h2>
          <p className="pt-1 text-sm text-fg-muted">
            A guard that has refused nothing for {days} days is either unnecessary or broken, and a guard nothing
            reached at all may not be wired in. Both are worth ten minutes.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            {rates.filter((g) => g.silentSevenDays).map((g) => <Pill key={g.guard} tone="warn">{g.guard}</Pill>)}
            {rates.filter((g) => g.neverReached).map((g) => <Pill key={g.guard} tone="danger">{g.guard}</Pill>)}
          </div>
        </div>
      )}
    </section>
  );
}

/* --------------------------------------------------------------------- replays */

function ReplaysTab(
  { golden, personas }: {
    golden: { id: string; ok: boolean; cases: number; casesFailed: number; assertions: number; assertionsFailed: number; actor: string; at: string; digest: string | null; failed: { id: string; title: string; pins: string }[] }[];
    personas: { id: string; persona: string; mode: string; turns: number; ok: boolean; checksFailed: number; egressAttempts: number; storesUntouched: boolean; failed: { name: string; detail: string }[]; at: string }[];
  },
): ReactNode {
  return (
    <section className="flex flex-col gap-3">
      <div className={CARD}>
        <h2 className="pb-2 text-sm font-semibold text-fg">Golden suite</h2>
        {golden.length === 0
          ? (
            <p className="text-sm text-fg-muted">
              No run has been recorded. The gate is <code>h1 golden run &lt;tenant&gt;</code>; every run it
              makes lands here.
            </p>
          )
          : golden.map((g) => (
            <div key={g.id} className="border-t border-line py-2 first:border-0">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone={g.ok ? 'ok' : 'danger'}>{g.ok ? 'green' : `${g.casesFailed} failed`}</Pill>
                <span className="text-sm text-fg">{g.cases} cases · {g.assertions} assertions</span>
                <span className="text-xs text-fg-subtle">{formatDateTime(g.at)} · {g.actor}</span>
                {g.digest && <span className="text-xs text-fg-subtle">pack {g.digest.slice(0, 12)}</span>}
              </div>
              {g.failed.map((c) => (
                <p key={c.id} className="pt-1 text-sm text-danger-soft-fg">
                  <span className="font-medium">{c.id}</span> — {c.title}. Pins: {c.pins}
                </p>
              ))}
            </div>
          ))}
      </div>

      <div className={CARD}>
        <h2 className="pb-2 text-sm font-semibold text-fg">Persona Lab</h2>
        <p className="pb-2 text-sm text-fg-muted">
          Each persona is run twice: once with a model that follows the directive, and once with one that ignores
          it completely. The second run is the one that says what the guards do when the advice is not taken.
        </p>
        {personas.length === 0
          ? <p className="text-sm text-fg-muted">No persona run has been recorded for this tenant.</p>
          : (
            <ul className="flex flex-col gap-2">
              {personas.map((p) => (
                <li key={p.id} className="border-t border-line pt-2 first:border-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-fg">{p.persona}</span>
                    <Pill tone={p.mode === 'defiant' ? 'warn' : 'neutral'}>{p.mode}</Pill>
                    <Pill tone={p.ok ? 'ok' : 'danger'}>{p.ok ? 'clean' : `${p.checksFailed} failed`}</Pill>
                    <span className="text-xs text-fg-subtle">{p.turns} turns · {formatDateTime(p.at)}</span>
                    {p.egressAttempts > 0 && <Pill tone="danger">{p.egressAttempts} egress attempts</Pill>}
                    {!p.storesUntouched && <Pill tone="danger">stores moved</Pill>}
                  </div>
                  {p.failed.map((c) => (
                    <p key={c.name} className="pt-1 text-sm text-danger-soft-fg">{c.name}: {c.detail}</p>
                  ))}
                </li>
              ))}
            </ul>
          )}
      </div>
    </section>
  );
}
